import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { THEME_CONFIG } from '../config/constants';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  theme: ThemeMode;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = useLocalStorage<ThemeMode>(THEME_CONFIG.localStorageKey, THEME_CONFIG.defaultMode);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') return 'light';
    const savedTheme = localStorage.getItem(THEME_CONFIG.localStorageKey) as ThemeMode || THEME_CONFIG.defaultMode;
    return savedTheme === 'system' ? (prefersDark ? 'dark' : 'light') : savedTheme;
  });

  // Apply theme to document
  const applyTheme = (themeToApply: 'light' | 'dark') => {
    const root = document.documentElement;
    if (themeToApply === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    setResolvedTheme(themeToApply);
  };

  // Update resolved theme when theme or system preference changes
  useEffect(() => {
    const resolved = theme === 'system' ? (prefersDark ? 'dark' : 'light') : theme;
    applyTheme(resolved);
  }, [theme, prefersDark]);

  // Update theme and apply changes
  const updateTheme = (newTheme: ThemeMode) => {
    setTheme(newTheme);
  };

  // Toggle between light and dark (skips system)
  const toggleTheme = () => {
    if (theme === 'system') {
      // If currently on system, switch to opposite of current resolved theme
      updateTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
    } else {
      // Toggle between light and dark
      updateTheme(theme === 'dark' ? 'light' : 'dark');
    }
  };

  const value: ThemeContextValue = {
    theme,
    resolvedTheme,
    setTheme: updateTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
